import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { Platform } from "react-native";
import Constants from "expo-constants";
import * as Updates from "expo-updates";
import { buildClientInfo, type ClientPlatform } from "../utils/clientInfo";

/**
 * The device's IANA timezone (e.g. "America/New_York"), or undefined if the
 * runtime can't resolve one. Backend reminder emails use it to name a real
 * local start time (Meldpot #76).
 */
export function getDeviceTimezone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && tz.length > 0 ? tz : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Upserts the signed-in user's current timezone onto their user doc. Called on
 * every login/app-open so it stays fresh if they travel. No-op for anonymous
 * users (they have no user doc) or when the timezone can't be resolved.
 */
export async function saveUserTimezone(uid: string): Promise<void> {
  const timezone = getDeviceTimezone();
  if (!timezone) return;
  try {
    await setDoc(
      doc(db, "users", uid),
      { timezone, timezoneUpdatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    console.warn("Failed to save user timezone:", err);
  }
}

/**
 * Records which app version/OTA this device is running on the user doc, under
 * clientInfoByPlatform.<platform> so iOS, Android and web don't overwrite each
 * other (merge keeps the other platforms' entries). Called on every
 * login/app-open alongside the timezone, so admins can see who is on what.
 */
export async function saveClientInfo(uid: string): Promise<void> {
  try {
    const info = buildClientInfo({
      // The app only ships ios/android/web.
      platform: Platform.OS as ClientPlatform,
      osVersion: Platform.OS === "web" ? null : String(Platform.Version),
      appVersion: Constants.expoConfig?.version,
      isUpdatesEnabled: Updates.isEnabled,
      runtimeVersion: Updates.runtimeVersion,
      channel: Updates.channel,
      updateId: Updates.updateId,
      updateCreatedAt: Updates.createdAt,
      isEmbeddedLaunch: Updates.isEmbeddedLaunch,
    });
    await setDoc(
      doc(db, "users", uid),
      {
        clientInfoByPlatform: {
          [info.platform]: { ...info, updatedAt: serverTimestamp() },
        },
      },
      { merge: true },
    );
  } catch (err) {
    console.warn("Failed to save client info:", err);
  }
}

export async function createUser(
  name: string,
  email: string,
  password: string,
) {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password,
  );

  const user = userCredential.user;

  // 2) Обновляем displayName в Firebase Auth
  try {
    await updateProfile(user, { displayName: name });
    try {
      await user.reload();
    } catch (reloadErr) {
      console.warn("Failed to reload user after profile update:", reloadErr);
    }
  } catch (profileErr) {
    console.error("Failed to update firebase profile displayName:", profileErr);
  }

  // 3) Создаём документ в Firestore в коллекции "users"
  try {
    const userDocRef = doc(db, "users", user.uid);
    await setDoc(userDocRef, {
      id: user.uid,
      email: user.email,
      name: name,
      createdAt: serverTimestamp(),
      timezone: getDeviceTimezone() ?? null,
    });
  } catch (firestoreErr) {
    console.error("Failed to save user document in Firestore:", firestoreErr);
  }
}

export const resetPassword = async (
  email: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error: any) {
    console.error("Error sending password reset email:", error);
    let errorMessage = "Failed to send password reset email. Please try again.";

    if (error.code === "auth/user-not-found") {
      errorMessage = "No account found with this email address.";
    } else if (error.code === "auth/invalid-email") {
      errorMessage = "Invalid email address format.";
    } else if (error.code === "auth/too-many-requests") {
      errorMessage = "Too many requests. Please try again later.";
    } else if (error.code === "auth/network-request-failed") {
      errorMessage =
        "Network error. Please check your connection and try again.";
    }

    return { success: false, error: errorMessage };
  }
};

/**
 * Persists the signed-in user's marketing-email opt-out preference (Meldpot
 * #103). true = opted out of tips/updates. Transactional emails are unaffected.
 */
export async function saveEmailOptOut(
  uid: string,
  optOut: boolean,
): Promise<void> {
  await setDoc(
    doc(db, "users", uid),
    { emailOptOut: optOut, emailOptOutUpdatedAt: serverTimestamp() },
    { merge: true },
  );
}

/**
 * Permanently deletes the signed-in user's own account (Meldpot #101). Calls
 * the backend, which removes the Firebase Auth login and all Firestore data,
 * then signs out locally so the app drops back to the guest/auth flow.
 */
export async function deleteMyAccount(): Promise<void> {
  const functions = getFunctions();
  const callable = httpsCallable(functions, "deleteUserAccount");
  await callable({});
  await firebaseSignOut(auth);
}

export const signOut = async (): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    await firebaseSignOut(auth);
    console.log("Sign out successful");
    return { success: true };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    console.error("Sign out error:", error);
    return { success: false, error: errorMessage };
  }
};
